import { t as __exportAll } from "./chunk-CfYAbeIz.mjs";
import micromatch from "micromatch";
import * as R from "ramda";
//#region src/constants.ts
const EMAIL_ATTRIBUTE = "email";
const GROUPS_ATTRIBUTE = "cognito:groups";
const STAGE_ATTRIBUTE = "custom:Stage";
const INTERNAL_ATTRIBUTE = "custom:Internal";
//#endregion
//#region ../../node_modules/typebox/build/system/memory/metrics.mjs
/** TypeBox instantiation metrics */
const Metrics = {
	assign: 0,
	create: 0,
	clone: 0,
	discard: 0,
	update: 0
};
//#endregion
//#region ../../node_modules/typebox/build/guard/guard.mjs
/** Returns true if this value is an array */
function IsArray(value) {
	return Array.isArray(value);
}
/** Returns true if this value is bigint */
function IsBigInt(value) {
	return IsEqual(typeof value, "bigint");
}
/** Returns true if this value is a boolean */
function IsBoolean(value) {
	return IsEqual(typeof value, "boolean");
}
/** Returns true if this value is null */
function IsNull(value) {
	return IsEqual(value, null);
}
/** Returns true if this value is number */
function IsNumber(value) {
	return Number.isFinite(value);
}
/** Returns true if this value is an object but not an array */
function IsObjectNotArray(value) {
	return IsObject(value) && !IsArray(value);
}
/** Returns true if this value is an object */
function IsObject(value) {
	return IsEqual(typeof value, "object") && !IsNull(value);
}
/** Returns true if this value is string */
function IsString(value) {
	return IsEqual(typeof value, "string");
}
function IsEqual(left, right) {
	return left === right;
}
/** Returns true if the PropertyKey is Unsafe (ref: prototype-pollution). */
function IsUnsafePropertyKey(key) {
	return IsEqual(key, "__proto__") || IsEqual(key, "constructor") || IsEqual(key, "prototype");
}
/** Returns true if this value has this property key */
function HasPropertyKey(value, key) {
	return IsUnsafePropertyKey(key) ? Object.prototype.hasOwnProperty.call(value, key) : key in value;
}
/** Returns property keys for this object via `Object.getOwnPropertyKeys({ ... })` */
function Keys(value) {
	return Object.getOwnPropertyNames(value);
}
//#endregion
//#region ../../node_modules/typebox/build/system/memory/clone.mjs
function IsGuard(value) {
	return IsObject(value) && HasPropertyKey(value, "~guard");
}
function FromGuard(value) {
	return value;
}
function FromArray(value) {
	return value.map((value) => FromValue(value));
}
function FromObject(value) {
	const result = {};
	const descriptors = Object.getOwnPropertyDescriptors(value);
	for (const key of Object.keys(descriptors)) {
		const descriptor = descriptors[key];
		if (HasPropertyKey(descriptor, "value")) Object.defineProperty(result, key, {
			...descriptor,
			value: FromValue(descriptor.value)
		});
	}
	return result;
}
function FromRegExp(value) {
	return new RegExp(value.source, value.flags);
}
function FromUnknown(value) {
	return value;
}
function FromValue(value) {
	return value instanceof RegExp ? FromRegExp(value) : IsGuard(value) ? FromGuard(value) : IsArray(value) ? FromArray(value) : IsObject(value) ? FromObject(value) : FromUnknown(value);
}
/**
* Clones a value using the TypeBox type cloning strategy. This function preserves non-enumerable
* properties from the source value. This is to ensure cloned types retain discriminable
* hidden properties.
*/
function Clone(value) {
	Metrics.clone += 1;
	return FromValue(value);
}
//#endregion
//#region ../../node_modules/typebox/build/system/settings/settings.mjs
const settings = {
	immutableTypes: false,
	maxErrors: 8,
	useAcceleration: true,
	exactOptionalPropertyTypes: false,
	enumerableKind: false,
	correctiveParse: false
};
/** Gets current system settings */
function Get() {
	return settings;
}
//#endregion
//#region ../../node_modules/typebox/build/system/memory/create.mjs
function MergeHidden(left, right) {
	for (const key of Object.keys(right)) Object.defineProperty(left, key, {
		configurable: true,
		writable: true,
		enumerable: false,
		value: right[key]
	});
	return left;
}
function Merge(left, right) {
	return {
		...left,
		...right
	};
}
/**
* Creates an object with hidden, enumerable, and optional property sets. This function
* ensures types are instantiated according to configuration rules for enumerable and
* non-enumerable properties.
*/
function Create(hidden, enumerable, options = {}) {
	Metrics.create += 1;
	const settings = Get();
	const withOptions = Merge(enumerable, options);
	const withHidden = settings.enumerableKind ? Merge(withOptions, hidden) : MergeHidden(withOptions, hidden);
	return settings.immutableTypes ? Object.freeze(withHidden) : withHidden;
}
//#endregion
//#region ../../node_modules/typebox/build/system/memory/update.mjs
/**
* Updates a value with new properties while preserving property enumerability. Use this function to modify
* existing types without altering their configuration.
*/
function Update(current, hidden, enumerable) {
	Metrics.update += 1;
	const settings = Get();
	const result = Clone(current);
	for (const key of Object.keys(hidden)) Object.defineProperty(result, key, {
		configurable: true,
		writable: true,
		enumerable: settings.enumerableKind,
		value: hidden[key]
	});
	for (const key of Object.keys(enumerable)) Object.defineProperty(result, key, {
		configurable: true,
		enumerable: true,
		writable: true,
		value: enumerable[key]
	});
	return result;
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/schema.mjs
function IsSchema(value) {
	return IsObject(value);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/_optional.mjs
/** Adds Optional to the given type. */
function OptionalAdd(type) {
	return Update(type, { "~optional": true }, {});
}
/** Applies an Optional modifier to the given type. */
function Optional(type) {
	return OptionalAdd(type);
}
/** Returns true if the given value is TOptional */
function IsOptional(value) {
	return IsSchema(value) && HasPropertyKey(value, "~optional");
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/array.mjs
/** Creates an Array type. */
function _Array_(items, options) {
	return Create({ "~kind": "Array" }, {
		type: "array",
		items
	}, options);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/properties.mjs
/** Creates a RequiredArray derived from the given TProperties value. */
function RequiredArray(properties) {
	return Keys(properties).filter((key) => !IsOptional(properties[key]));
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/object.mjs
/** Creates an Object type. */
function _Object_(properties, options = {}) {
	const requiredKeys = RequiredArray(properties);
	const required = requiredKeys.length > 0 ? { required: requiredKeys } : {};
	return Create({ "~kind": "Object" }, {
		type: "object",
		...required,
		properties
	}, options);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/union.mjs
/** Creates a Union type. */
function Union(anyOf, options = {}) {
	return Create({ "~kind": "Union" }, { anyOf }, options);
}
//#endregion
//#region ../../node_modules/typebox/build/type/engine/enum/typescript_enum_to_enum_values.mjs
function IsTypeScriptEnumLike(value) {
	return IsObjectNotArray(value);
}
function TypeScriptEnumToEnumValues(type) {
	return Keys(type).filter((key) => isNaN(key)).reduce((result, key) => [...result, type[key]], []);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/enum.mjs
/** Creates an Enum type. */
function Enum(value, options) {
	const values = IsTypeScriptEnumLike(value) ? TypeScriptEnumToEnumValues(value) : value;
	return Create({ "~kind": "Enum" }, { enum: values }, options);
}
//#endregion
//#region ../../node_modules/typebox/build/system/hashing/hash.mjs
var ByteMarker;
(function(ByteMarker) {
	ByteMarker[ByteMarker["Array"] = 0] = "Array";
	ByteMarker[ByteMarker["BigInt"] = 1] = "BigInt";
	ByteMarker[ByteMarker["Boolean"] = 2] = "Boolean";
	ByteMarker[ByteMarker["Date"] = 3] = "Date";
	ByteMarker[ByteMarker["Constructor"] = 4] = "Constructor";
	ByteMarker[ByteMarker["Function"] = 5] = "Function";
	ByteMarker[ByteMarker["Null"] = 6] = "Null";
	ByteMarker[ByteMarker["Number"] = 7] = "Number";
	ByteMarker[ByteMarker["Object"] = 8] = "Object";
	ByteMarker[ByteMarker["RegExp"] = 9] = "RegExp";
	ByteMarker[ByteMarker["String"] = 10] = "String";
	ByteMarker[ByteMarker["Symbol"] = 11] = "Symbol";
	ByteMarker[ByteMarker["TypeArray"] = 12] = "TypeArray";
	ByteMarker[ByteMarker["Undefined"] = 13] = "Undefined";
})(ByteMarker || (ByteMarker = {}));
Array.from({ length: 256 }).map((_, i) => BigInt(i));
const F64 = new Float64Array(1);
new DataView(F64.buffer);
new Uint8Array(F64.buffer);
new TextEncoder();
//#endregion
//#region ../../node_modules/typebox/build/type/types/integer.mjs
const IntegerPattern = "-?(?:0|[1-9][0-9]*)";
//#endregion
//#region ../../node_modules/typebox/build/type/types/literal.mjs
var InvalidLiteralValue = class extends Error {
	constructor(value) {
		super(`Invalid Literal value`);
		Object.defineProperty(this, "cause", {
			value: { value },
			writable: false,
			configurable: false,
			enumerable: false
		});
	}
};
function LiteralTypeName(value) {
	return IsBigInt(value) ? "bigint" : IsBoolean(value) ? "boolean" : IsNumber(value) ? "number" : IsString(value) ? "string" : (() => {
		throw new InvalidLiteralValue(value);
	})();
}
/** Creates a Literal type. */
function Literal(value, options) {
	return Create({ "~kind": "Literal" }, {
		type: LiteralTypeName(value),
		const: value
	}, options);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/number.mjs
const NumberPattern = "-?(?:0|[1-9][0-9]*)(?:.[0-9]+)?";
//#endregion
//#region ../../node_modules/typebox/build/type/types/string.mjs
/** Creates a String type. */
function String$1(options) {
	return Create({ "~kind": "String" }, { type: "string" }, options);
}
//#endregion
//#region ../../node_modules/typebox/build/type/types/record.mjs
const IntegerKey = `^${IntegerPattern}$`;
`${NumberPattern}`;
//#endregion
//#region ../../node_modules/typebox/build/type/script/token/internal/char.mjs
function Range(start, end) {
	return Array.from({ length: end - start + 1 }, (_, i) => String.fromCharCode(start + i));
}
const Alpha = [...Range(97, 122), ...Range(65, 90)];
const Digit = ["0", ...Range(49, 57)];
[...Digit];
[...[
	...Alpha,
	"_",
	"$"
], ...Digit];
[...Digit];
new RegExp(IntegerKey);
Enum([
	"R",
	"RW",
	"ADMIN",
	"OWNER",
	"DENY"
]);
const LLPermissionSchema = Union([_Object_({
	name: String$1({ pattern: /^[^*]*\*?$/ }),
	permission: Enum([
		"R",
		"RW",
		"ADMIN",
		"OWNER"
	])
}), _Object_({
	name: String$1({ pattern: /^[^*]*$/ }),
	permission: Literal("DENY")
})], { title: "LLIAM Permission" });
_Object_({
	groups: Optional(_Array_(LLPermissionSchema)),
	groupMembership: Optional(_Array_(LLPermissionSchema)),
	objectstores: Optional(_Array_(LLPermissionSchema)),
	beacons: Optional(_Array_(LLPermissionSchema)),
	mapnotes: Optional(_Array_(LLPermissionSchema)),
	assets: Optional(_Array_(LLPermissionSchema)),
	internaltools: Optional(_Array_(LLPermissionSchema)),
	mapsyndication: Optional(_Array_(LLPermissionSchema)),
	mapsondisplay: Optional(_Array_(LLPermissionSchema)),
	people_insight_dashboards: Optional(_Array_(LLPermissionSchema))
}, { title: "LLIAM Policy" });
//#endregion
//#region ../../node_modules/typebox/build/format/date.mjs
const DAYS = [
	0,
	31,
	28,
	31,
	30,
	31,
	30,
	31,
	31,
	30,
	31,
	30,
	31
];
const DATE = /^(\d\d\d\d)-(\d\d)-(\d\d)$/;
function IsLeapYear(year) {
	return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
/**
* Returns true if the value is a ISO8601 Date component string
* @source ajv-formats
* @example `2020-12-12`
*/
function IsDate(value) {
	const matches = DATE.exec(value);
	if (!matches) return false;
	const year = +matches[1];
	const month = +matches[2];
	const day = +matches[3];
	return month >= 1 && month <= 12 && day >= 1 && day <= (month === 2 && IsLeapYear(year) ? 29 : DAYS[month]);
}
//#endregion
//#region ../../node_modules/typebox/build/format/time.mjs
const TIME = /^(\d\d):(\d\d):(\d\d(?:\.\d+)?)(?:Z|([+-])(\d\d):(\d\d))?$/i;
/**
* Returns true if the value is a ISO time string
* @specification
*/
function IsTime(value, strictTimeZone = true) {
	const matches = TIME.exec(value);
	if (!matches) return false;
	const hr = +matches[1];
	const min = +matches[2];
	const sec = +matches[3];
	const tzSign = matches[4] === "-" ? -1 : 1;
	const tzH = +(matches[5] || 0);
	const tzM = +(matches[6] || 0);
	if (tzH > 23 || tzM > 59) return false;
	if (strictTimeZone && !matches[4] && value.toLowerCase().indexOf("z") === -1) return false;
	if (hr <= 23 && min <= 59 && sec < 60) return true;
	const utcMin = min - tzM * tzSign;
	const utcHr = hr - tzH * tzSign - (utcMin < 0 ? 1 : 0);
	return (utcHr === 23 || utcHr === -1) && (utcMin === 59 || utcMin === -1) && sec < 61;
}
//#endregion
//#region ../../node_modules/typebox/build/format/date_time.mjs
/**
* Returns true if the value is a ISO8601 DateTime string
* @source ajv-formats
* @example `2020-12-12T20:20:40+00:00`
*/
function IsDateTime(value, strictTimeZone = true) {
	const dateTime = value.split(/T/i);
	return dateTime.length === 2 && IsDate(dateTime[0]) && IsTime(dateTime[1], strictTimeZone);
}
//#endregion
//#region ../../node_modules/typebox/build/format/duration.mjs
const Duration = /^P((\d+Y(\d+M(\d+D)?)?|\d+M(\d+D)?|\d+D)(T(\d+H(\d+M(\d+S)?)?|\d+M(\d+S)?|\d+S))?|T(\d+H(\d+M(\d+S)?)?|\d+M(\d+S)?|\d+S)|\d+W)$/;
/**
* Returns true if the value is a valid ISO-8601 duration.
* @specification https://tools.ietf.org/html/rfc3339
*/
function IsDuration(value) {
	return Duration.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/email.mjs
const Email = /^(?!.*\.\.)[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;
/**
* Returns true if the value is an Email
* @specification ajv-formats
*/
function IsEmail(value) {
	return Email.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/_puny.mjs
const PUNYCODE_BASE = 36;
const PUNYCODE_TMIN = 1;
const PUNYCODE_TMAX = 26;
const PUNYCODE_SKEW = 38;
const PUNYCODE_DAMP = 700;
const PUNYCODE_INITIAL_BIAS = 72;
const PUNYCODE_INITIAL_N = 128;
function Adapt(delta, numPoints, firstTime) {
	delta = firstTime ? Math.floor(delta / PUNYCODE_DAMP) : delta >> 1;
	delta += Math.floor(delta / numPoints);
	let k = 0;
	while (delta > (PUNYCODE_BASE - PUNYCODE_TMIN) * PUNYCODE_TMAX >> 1) {
		delta = Math.floor(delta / (PUNYCODE_BASE - PUNYCODE_TMIN));
		k += PUNYCODE_BASE;
	}
	return k + Math.floor((PUNYCODE_BASE - PUNYCODE_TMIN + 1) * delta / (delta + PUNYCODE_SKEW));
}
function Decode(value) {
	const output = [];
	let n = PUNYCODE_INITIAL_N;
	let i = 0;
	let bias = PUNYCODE_INITIAL_BIAS;
	const delimIdx = value.lastIndexOf("-");
	if (delimIdx > 0) for (let j = 0; j < delimIdx; j++) {
		const cp = value.charCodeAt(j);
		if (cp >= 128) throw new Error("Invalid punycode: non-basic before delimiter");
		output.push(cp);
	}
	let inIdx = delimIdx < 0 ? 0 : delimIdx + 1;
	while (inIdx < value.length) {
		const oldi = i;
		let w = 1;
		let k = PUNYCODE_BASE;
		while (true) {
			if (inIdx >= value.length) throw new Error("Invalid punycode: unexpected end of input");
			const ch = value.charCodeAt(inIdx++);
			let digit;
			if (ch >= 97 && ch <= 122) digit = ch - 97;
			else if (ch >= 48 && ch <= 57) digit = ch - 48 + 26;
			else if (ch >= 65 && ch <= 90) digit = ch - 65;
			else throw new Error("Invalid punycode: bad digit character");
			i += digit * w;
			const t = k <= bias ? PUNYCODE_TMIN : k >= bias + PUNYCODE_TMAX ? PUNYCODE_TMAX : k - bias;
			if (digit < t) break;
			w *= PUNYCODE_BASE - t;
			k += PUNYCODE_BASE;
		}
		const outLen = output.length + 1;
		bias = Adapt(i - oldi, outLen, oldi === 0);
		n += Math.floor(i / outLen);
		i %= outLen;
		output.splice(i, 0, n);
		i++;
	}
	return globalThis.String.fromCodePoint(...output);
}
//#endregion
//#region ../../node_modules/typebox/build/format/_idna.mjs
function IsNonspacingMark(cp) {
	return /\p{Mn}/u.test(String.fromCodePoint(cp));
}
function IsSpacingCombiningMark(cp) {
	return /\p{Mc}/u.test(String.fromCodePoint(cp));
}
function IsEnclosingMark(cp) {
	return /\p{Me}/u.test(String.fromCodePoint(cp));
}
function IsCombiningMark(cp) {
	return IsNonspacingMark(cp) || IsSpacingCombiningMark(cp) || IsEnclosingMark(cp);
}
const RFC5892_DISALLOWED = new Set([
	1600,
	2042,
	12334,
	12335,
	12337,
	12338,
	12339,
	12340,
	12341,
	12347
]);
const VIRAMA_CPS = new Set([
	2381,
	2509,
	2637,
	2765,
	2893,
	3021,
	3149,
	3277,
	3387,
	3388,
	3405,
	3530,
	6980,
	7082,
	7083,
	43456,
	69702,
	69759,
	69817,
	69939,
	69940,
	70080,
	70197,
	70477,
	70722,
	70850,
	71103,
	71231,
	71350,
	72767,
	73028,
	73029
]);
function IsGreek(cp) {
	return /\p{Script=Greek}/u.test(String.fromCodePoint(cp));
}
function IsHebrew(cp) {
	return /\p{Script=Hebrew}/u.test(String.fromCodePoint(cp));
}
function IsHiragana(cp) {
	return /\p{Script=Hiragana}/u.test(String.fromCodePoint(cp));
}
function IsKatakana(cp) {
	return /\p{Script=Katakana}/u.test(String.fromCodePoint(cp));
}
function IsHan(cp) {
	return /\p{Script=Han}/u.test(String.fromCodePoint(cp));
}
function IsArabicIndicDigit(cp) {
	return cp >= 1632 && cp <= 1641;
}
function IsExtendedArabicIndicDigit(cp) {
	return cp >= 1776 && cp <= 1785;
}
function IsVirama(cp) {
	return VIRAMA_CPS.has(cp);
}
function IsUnicodeLabel(value) {
	if (value.length === 0) return false;
	const cps = [...value].map((c) => c.codePointAt(0));
	const len = cps.length;
	if (cps[0] === 45 || cps[len - 1] === 45) return false;
	if (len >= 4 && cps[2] === 45 && cps[3] === 45) return false;
	if (IsCombiningMark(cps[0])) return false;
	let hasJapanese = false;
	let hasArabicIndic = false;
	let hasExtendedArabicIndic = false;
	for (let i = 0; i < len; i++) {
		const cp = cps[i];
		if (RFC5892_DISALLOWED.has(cp)) return false;
		if (IsHiragana(cp) || IsKatakana(cp) || IsHan(cp)) hasJapanese = true;
		if (IsArabicIndicDigit(cp)) hasArabicIndic = true;
		if (IsExtendedArabicIndicDigit(cp)) hasExtendedArabicIndic = true;
		const prev = cps[i - 1], next = cps[i + 1];
		switch (cp) {
			case 183:
				if (prev !== 108 || next !== 108) return false;
				break;
			case 885:
				if (next === void 0 || !IsGreek(next)) return false;
				break;
			case 1523:
			case 1524:
				if (prev === void 0 || !IsHebrew(prev)) return false;
				break;
			case 8205:
				if (prev === void 0 || !IsVirama(prev)) return false;
				break;
			case 12539: break;
		}
	}
	if (value.includes("・") && !hasJapanese) return false;
	if (hasArabicIndic && hasExtendedArabicIndic) return false;
	return true;
}
function IsAsciiLabel(value) {
	if (value.charCodeAt(0) === 45 || value.charCodeAt(value.length - 1) === 45) return false;
	if (value.length >= 4 && value.charCodeAt(2) === 45 && value.charCodeAt(3) === 45) return false;
	for (let i = 0; i < value.length; i++) {
		const ch = value.charCodeAt(i);
		if (!(ch >= 97 && ch <= 122 || ch >= 65 && ch <= 90 || ch >= 48 && ch <= 57 || ch === 45)) return false;
	}
	return true;
}
function IsPuny(value) {
	return value.toLowerCase().startsWith("xn--");
}
function IsPunyLabel(value) {
	try {
		return IsUnicodeLabel(Decode(value.slice(4)));
	} catch {
		return false;
	}
}
function IsIdnLabel(value) {
	if (value.length === 0 || value.length > 63) return false;
	return IsPuny(value) ? IsPunyLabel(value) : IsUnicodeLabel(value);
}
function IsLabel(value) {
	if (value.length === 0 || value.length > 63) return false;
	return IsPuny(value) ? IsPunyLabel(value) : IsAsciiLabel(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/hostname.mjs
/**
* Returns true if the value is a valid hostname.
* @specification https://tools.ietf.org/html/rfc1123
* @specification https://tools.ietf.org/html/rfc5891
* @specification https://tools.ietf.org/html/rfc5892
*/
function IsHostname(value) {
	if (value.length === 0 || value.length > 253) return false;
	if (value.charCodeAt(value.length - 1) === 46) return false;
	for (const label of value.split(".")) if (!IsLabel(label)) return false;
	return true;
}
//#endregion
//#region ../../node_modules/typebox/build/format/idn_email.mjs
const IdnEmail = /^(?!.*\.\.)[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+(?:\.[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+)*@[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?(?:\.[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?)*$/iu;
/**
* Returns true if the value is an IdnEmail
* @specification ajv-formats (unicode-extension)
*/
function IsIdnEmail(value) {
	return IdnEmail.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/idn_hostname.mjs
/**
* Returns true if the value is a valid internationalized (IDN) hostname.
* @specification https://tools.ietf.org/html/rfc3490
* @specification https://tools.ietf.org/html/rfc5891
* @specification https://tools.ietf.org/html/rfc5892
*/
function IsIdnHostname(value) {
	if (value.length === 0 || value.includes(" ")) return false;
	const canonical = value.normalize("NFC").replace(/[\u002E\u3002\uFF0E\uFF61]/g, ".");
	if (canonical.length > 253) return false;
	for (const label of canonical.split(".")) if (!IsIdnLabel(label)) return false;
	return true;
}
//#endregion
//#region ../../node_modules/typebox/build/format/ipv4.mjs
function IsIPv4Internal(value, start, end) {
	let dots = 0;
	let num = 0;
	let digits = 0;
	let leading = 0;
	for (let i = start; i < end; i++) {
		const ch = value.charCodeAt(i);
		if (ch === 46) {
			if (digits === 0 || num > 255 || leading === 48 && digits > 1) return false;
			dots++;
			num = 0;
			digits = 0;
			leading = 0;
		} else if (ch >= 48 && ch <= 57) {
			if (digits === 0) leading = ch;
			num = num * 10 + (ch - 48);
			digits++;
		} else return false;
	}
	return dots === 3 && digits > 0 && num <= 255 && !(leading === 48 && digits > 1);
}
/**
* Returns true if the value is a IPV4 address
* @specification http://tools.ietf.org/html/rfc2673#section-3.2
*/
function IsIPv4(value) {
	return IsIPv4Internal(value, 0, value.length);
}
//#endregion
//#region ../../node_modules/typebox/build/format/ipv6.mjs
function InRange(ch) {
	return ch >= 48 && ch <= 57 || ch >= 65 && ch <= 70 || ch >= 97 && ch <= 102;
}
/**
* Returns true if the value is an IPv6 address
* @specification http://tools.ietf.org/html/rfc2373#section-2.2
*/
function IsIPv6(value) {
	const length = value.length;
	if (length === 0) return false;
	let groups = 0;
	let compressed = false;
	let i = 0;
	if (value.charCodeAt(0) === 58 && value.charCodeAt(1) === 58) {
		if (length === 2) return true;
		compressed = true;
		i = 2;
	}
	while (i < length) {
		let digits = 0;
		const start = i;
		while (i < length && InRange(value.charCodeAt(i))) {
			i++;
			digits++;
		}
		if (digits === 0) return false;
		const next = value.charCodeAt(i);
		if (next === 46) {
			if (!IsIPv4Internal(value, start, length)) return false;
			groups += 2;
			i = length;
			break;
		}
		if (digits > 4) return false;
		groups++;
		if (i === length) break;
		if (next !== 58) return false;
		i++;
		if (value.charCodeAt(i) === 58) {
			if (compressed) return false;
			if (value.charCodeAt(i + 1) === 58) return false;
			compressed = true;
			i++;
			if (i === length) break;
		}
	}
	return compressed ? groups <= 7 : groups === 8;
}
//#endregion
//#region ../../node_modules/typebox/build/format/iri_reference.mjs
function TryUrl(value) {
	try {
		new URL(value, "http://example.com");
		return true;
	} catch {
		return false;
	}
}
/**
* Returns true if the value is a Iri reference
* @specification
*/
function IsIriReference(value) {
	if (value.includes(" ")) return false;
	if (value.includes("\\")) return false;
	if (/[\x00-\x1F\x7F]/.test(value)) return false;
	if (/%(?![0-9a-fA-F]{2})/.test(value)) return false;
	if (value === "") return true;
	const colonIndex = value.indexOf(":");
	if (colonIndex > 0 && /^[a-zA-Z][a-zA-Z0-9+\-.]*$/.test(value.substring(0, colonIndex))) return TryUrl(value);
	else {
		if (value.match(/^([a-zA-Z][a-zA-Z0-9+\-.]*)(\/\/)/) && colonIndex === -1) return false;
		return TryUrl(value);
	}
}
//#endregion
//#region ../../node_modules/typebox/build/format/iri.mjs
/**
* Returns true if the value is a Iri
* @specification
*/
function IsIri(value) {
	try {
		new URL(value);
		return true;
	} catch {
		return false;
	}
}
//#endregion
//#region ../../node_modules/typebox/build/format/json_pointer_uri_fragment.mjs
const JsonPointerUriFragment = /^#(?:\/(?:[a-z0-9_\-.!$&'()*+,;:=@]|%[0-9a-f]{2}|~0|~1)*)*$/i;
/**
* Returns true if the value is a json pointer uri fragment
* @specification
* @source ajv-formats
*/
function IsJsonPointerUriFragment(value) {
	return JsonPointerUriFragment.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/json_pointer.mjs
const JsonPointer = /^(?:\/(?:[^~/]|~0|~1)*)*$/;
/**
* Returns true if the value is a json pointer
* @specification
* @source ajv-formats
*/
function IsJsonPointer(value) {
	return JsonPointer.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/regex.mjs
/**
* Returns true if the value is a regular expression string pattern
* @specification
* @source ajv-formats
*/
function IsRegex(value) {
	if (value.length === 0) return false;
	try {
		new RegExp(value);
		return true;
	} catch {
		return false;
	}
}
//#endregion
//#region ../../node_modules/typebox/build/format/relative_json_pointer.mjs
const RelativeJsonPointer = /^(?:0|[1-9][0-9]*)(?:#|(?:\/(?:[^~/]|~0|~1)*)*)$/;
/**
* Returns true if the value is a relative json pointer
* @specification
* @source ajv-formats
*/
function IsRelativeJsonPointer(value) {
	return RelativeJsonPointer.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/uri_reference.mjs
const UriReference = /^(?!.*[^\x00-\x7F])(?!.*\\)(?:(?:[a-z][a-z0-9+\-.]*:)?(?:\/\/[^\s[\]{}<>^`|]*)?|[^\s[\]{}<>^`|]*)(?:\?[^\s[\]{}<>^`|]*)?(?:#[^\s[\]{}<>^`|]*)?$/i;
/**
* Returns true if the value is a valid URI Reference.
* @specification https://tools.ietf.org/html/rfc3986
*/
function IsUriReference(value) {
	return UriReference.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/uri_template.mjs
const UriTemplate = /^(?:(?:[^\x00-\x20"'<>%\\^`{|}]|%[0-9a-f]{2})|\{[+#./;?&=,!@|]?(?:[a-z0-9_]|%[0-9a-f]{2})+(?::[1-9][0-9]{0,3}|\*)?(?:,(?:[a-z0-9_]|%[0-9a-f]{2})+(?::[1-9][0-9]{0,3}|\*)?)*\})*$/i;
/**
* Returns true if the value is a uri template
* @specification
* @source ajv-formats
*/
function IsUriTemplate(value) {
	return UriTemplate.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/uri.mjs
function IsAlpha(ch) {
	return ch >= 97 && ch <= 122 || ch >= 65 && ch <= 90;
}
function IsAlphaNumeric(ch) {
	return IsAlpha(ch) || ch >= 48 && ch <= 57;
}
function IsHex(ch) {
	return ch >= 48 && ch <= 57 || ch >= 65 && ch <= 70 || ch >= 97 && ch <= 102;
}
function IsSchemeChar(ch) {
	return IsAlphaNumeric(ch) || ch === 43 || ch === 45 || ch === 46;
}
function IsUnreserved(ch) {
	return IsAlphaNumeric(ch) || ch === 45 || ch === 46 || ch === 95 || ch === 126;
}
function IsSubDelim(ch) {
	return ch === 33 || ch === 36 || ch === 38 || ch === 39 || ch === 40 || ch === 41 || ch === 42 || ch === 43 || ch === 44 || ch === 59 || ch === 61;
}
function IsPchar(ch) {
	return IsUnreserved(ch) || IsSubDelim(ch) || ch === 58 || ch === 64;
}
/**
* Returns true if the value matches RFC 3986 URI syntax.
* @specification https://tools.ietf.org/html/rfc3986
*/
function IsUri(value) {
	const length = value.length;
	if (length === 0) return false;
	if (!IsAlpha(value.charCodeAt(0))) return false;
	let i = 1;
	while (i < length) {
		const ch = value.charCodeAt(i);
		if (ch === 58) break;
		if (!IsSchemeChar(ch)) return false;
		i++;
	}
	if (value.charCodeAt(i) !== 58) return false;
	i++;
	if (value.charCodeAt(i) === 47 && value.charCodeAt(i + 1) === 47) {
		i += 2;
		const authorityStart = i;
		let atPos = -1;
		for (let j = i; j < length; j++) {
			const ch = value.charCodeAt(j);
			if (ch === 64) {
				atPos = j;
				break;
			}
			if (ch === 47 || ch === 63 || ch === 35) break;
		}
		if (atPos !== -1) {
			for (let j = authorityStart; j < atPos; j++) {
				const ch = value.charCodeAt(j);
				if (ch === 91 || ch === 93) return false;
				if (ch === 37) {
					if (j + 2 >= atPos || !IsHex(value.charCodeAt(j + 1)) || !IsHex(value.charCodeAt(j + 2))) return false;
					j += 2;
				} else if (!IsUnreserved(ch) && !IsSubDelim(ch) && ch !== 58) return false;
			}
			i = atPos + 1;
		}
		if (value.charCodeAt(i) === 91) {
			i++;
			while (i < length && value.charCodeAt(i) !== 93) i++;
			if (value.charCodeAt(i) !== 93) return false;
			i++;
		} else while (i < length) {
			const ch = value.charCodeAt(i);
			if (ch === 47 || ch === 63 || ch === 35 || ch === 58) break;
			if (ch < 128 && !IsUnreserved(ch) && !IsSubDelim(ch)) return false;
			i++;
		}
		if (value.charCodeAt(i) === 58) {
			i++;
			while (i < length) {
				const ch = value.charCodeAt(i);
				if (ch === 47 || ch === 63 || ch === 35) break;
				if (ch < 48 || ch > 57) return false;
				i++;
			}
		}
	}
	while (i < length) {
		const ch = value.charCodeAt(i);
		if (ch === 37) {
			if (i + 2 >= length || !IsHex(value.charCodeAt(i + 1)) || !IsHex(value.charCodeAt(i + 2))) return false;
			i += 2;
		} else if (ch > 127) return false;
		else if (!(IsPchar(ch) || ch === 47 || ch === 63 || ch === 35)) return false;
		i++;
	}
	return true;
}
//#endregion
//#region ../../node_modules/typebox/build/format/url.mjs
const Url = /^(?:https?|ftp):\/\/(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u{00a1}-\u{ffff}]+-)*[a-z0-9\u{00a1}-\u{ffff}]+)(?:\.(?:[a-z0-9\u{00a1}-\u{ffff}]+-)*[a-z0-9\u{00a1}-\u{ffff}]+)*(?:\.(?:[a-z\u{00a1}-\u{ffff}]{2,})))(?::\d{2,5})?(?:\/[^\s]*)?$/iu;
/**
* Returns true if the value is a Url
* @specification
* @source ajv-formats
*/
function IsUrl(value) {
	return Url.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/uuid.mjs
const Uuid = /^(?:urn:uuid:)?[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
/**
* Returns true if the value is a uuid
* @specification
* @source ajv-formats
*/
function IsUuid(value) {
	return Uuid.test(value);
}
//#endregion
//#region ../../node_modules/typebox/build/format/_registry.mjs
const formats = /* @__PURE__ */ new Map();
/** Clears all entries */
function Clear() {
	formats.clear();
}
/** Resets all formats to defaults */
function Reset() {
	Clear();
	formats.set("date-time", IsDateTime);
	formats.set("date", IsDate);
	formats.set("duration", IsDuration);
	formats.set("email", IsEmail);
	formats.set("hostname", IsHostname);
	formats.set("idn-email", IsIdnEmail);
	formats.set("idn-hostname", IsIdnHostname);
	formats.set("ipv4", IsIPv4);
	formats.set("ipv6", IsIPv6);
	formats.set("iri-reference", IsIriReference);
	formats.set("iri", IsIri);
	formats.set("json-pointer-uri-fragment", IsJsonPointerUriFragment);
	formats.set("json-pointer", IsJsonPointer);
	formats.set("regex", IsRegex);
	formats.set("relative-json-pointer", IsRelativeJsonPointer);
	formats.set("time", IsTime);
	formats.set("uri-reference", IsUriReference);
	formats.set("uri-template", IsUriTemplate);
	formats.set("uri", IsUri);
	formats.set("url", IsUrl);
	formats.set("uuid", IsUuid);
}
Reset();
//#endregion
//#region src/validators/llpermission.ts
let External$1 = [];
function SetExternal$1(external) {
	External$1 = external.variables;
}
const check_c90a12175aaceaaa = ((value) => typeof value === "object" && value !== null && !Array.isArray(value) && "name" in value && "permission" in value && typeof value.name === "string" && External$1[0].test(value.name) && (value.permission === "R" || value.permission === "RW" || value.permission === "ADMIN" || value.permission === "OWNER") || typeof value === "object" && value !== null && !Array.isArray(value) && "name" in value && "permission" in value && typeof value.name === "string" && External$1[1].test(value.name) && typeof value.permission === "string" && value.permission === "DENY");
function Check$1(value) {
	return check_c90a12175aaceaaa(value);
}
SetExternal$1({ variables: [/^[^*]*\*?$/, /^[^*]*$/] });
//#endregion
//#region src/llpermission.ts
const PERMISSIONS = {
	OWNER: 5,
	DENY: 4,
	ADMIN: 3,
	RW: 1,
	R: 0
};
const PERMISSION_LEVELS = {
	OWNER: "OWNER",
	DENY: "DENY",
	ADMIN: "ADMIN",
	READWRITE: "RW",
	READ: "R"
};
const OWNER = PERMISSION_LEVELS.OWNER;
const DENY = PERMISSION_LEVELS.DENY;
const ADMIN = PERMISSION_LEVELS.ADMIN;
const READWRITE = PERMISSION_LEVELS.READWRITE;
const READ = PERMISSION_LEVELS.READ;
/**
* @param array
* @param attribute
* @returns that only contains objects with unique values for given "attribute"
*/
const removeDuplicatesByAttribute = (array, attribute) => R.uniqBy(R.path([attribute]), array);
/**
* @param permissionArray array of permissions
* @returns array of permissions, sorted in order of permission level, with no redundant permissions
*/
const removeRedundantPermissions = (permissionArray) => {
	return removeDuplicatesByAttribute(sortPermissionArray(permissionArray), "name");
};
/**
* @param permissionArray array of permissions
* @returns array of permissions, sorted in order of permission level.
*/
const sortPermissionArray = (permissionArray) => permissionArray.sort((a, b) => PERMISSIONS[b.permission] - PERMISSIONS[a.permission]);
/**
* @param permission the permission to check
* @param minimumPermission the minimum permission
* @returns check to see if permission is at least the minimum permission, and false if DENY
*/
const permissionIsAtLeast = (permission, minimumPermission) => {
	if (!permission) return false;
	if (permission === DENY) return false;
	return PERMISSIONS[permission] >= PERMISSIONS[minimumPermission];
};
/**
* @param resource of the resource
* @param permission to above resource
*/
const createPermission = (resource, permission) => ({
	name: resource,
	permission
});
/**
* @param permission to validate
* @returns representing if the policy is valid
*/
const validate = (permission) => {
	if (!permission) return false;
	return Check$1(permission);
};
//#endregion
//#region src/validators/llpolicy.ts
let External = [];
function SetExternal(external) {
	External = external.variables;
}
const check_83053120f172e935 = ((value) => typeof value === "object" && value !== null && !Array.isArray(value) && (value.groups === void 0 || !("groups" in value) || Array.isArray(value.groups) && value.groups.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[0].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[1].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.groupMembership === void 0 || !("groupMembership" in value) || Array.isArray(value.groupMembership) && value.groupMembership.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[2].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[3].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.objectstores === void 0 || !("objectstores" in value) || Array.isArray(value.objectstores) && value.objectstores.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[4].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[5].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.beacons === void 0 || !("beacons" in value) || Array.isArray(value.beacons) && value.beacons.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[6].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[7].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.mapnotes === void 0 || !("mapnotes" in value) || Array.isArray(value.mapnotes) && value.mapnotes.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[8].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[9].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.assets === void 0 || !("assets" in value) || Array.isArray(value.assets) && value.assets.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[10].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[11].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.internaltools === void 0 || !("internaltools" in value) || Array.isArray(value.internaltools) && value.internaltools.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[12].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[13].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.mapsyndication === void 0 || !("mapsyndication" in value) || Array.isArray(value.mapsyndication) && value.mapsyndication.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[14].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[15].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.mapsondisplay === void 0 || !("mapsondisplay" in value) || Array.isArray(value.mapsondisplay) && value.mapsondisplay.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[16].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[17].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")) && (value.people_insight_dashboards === void 0 || !("people_insight_dashboards" in value) || Array.isArray(value.people_insight_dashboards) && value.people_insight_dashboards.every((element, index) => typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[18].test(element.name) && (element.permission === "R" || element.permission === "RW" || element.permission === "ADMIN" || element.permission === "OWNER") || typeof element === "object" && element !== null && !Array.isArray(element) && "name" in element && "permission" in element && typeof element.name === "string" && External[19].test(element.name) && typeof element.permission === "string" && element.permission === "DENY")));
function Check(value) {
	return check_83053120f172e935(value);
}
SetExternal({ variables: [
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/,
	/^[^*]*\*?$/,
	/^[^*]*$/
] });
//#endregion
//#region src/llpolicy.ts
const PERMISSION_SETS = {
	OBJECTSTORES: "objectstores",
	GROUPS: "groups",
	GROUP_MEMBERSHIP: "groupMembership",
	MAPNOTES: "mapnotes",
	ASSETS: "assets",
	INTERNALTOOLS: "internaltools",
	BEACONS: "beacons",
	MAP_SYNDICATION: "mapsyndication",
	MAPS_ON_DISPLAY: "mapsondisplay",
	PEOPLE_INSIGHT_DASHBOARDS: "people_insight_dashboards"
};
const EMPTY_POLICY = {
	groups: [],
	groupMembership: [],
	objectstores: [],
	mapnotes: [],
	assets: [],
	beacons: [],
	internaltools: [],
	mapsyndication: [],
	mapsondisplay: [],
	people_insight_dashboards: []
};
/**
* @param policy
* @param permissionSet
* @returns array of permissions for this type, or if it doesn't exist, the empty policy permissions.
*/
const getPermissions = (policy, permissionSet) => policy[permissionSet] || EMPTY_POLICY[permissionSet];
/**
* @param a
* @param b
* @param reduce if the merged policy should be maximally reduced
* @returns policy where the permissions have been merged
*/
const mergePolicy = (a, b, reduce = true) => {
	const mergedPermissionSets = Object.values(PERMISSION_SETS).map((permissionSet) => {
		const permissionsA = getPermissions(a, permissionSet) ?? [];
		const permissionsB = getPermissions(b, permissionSet) ?? [];
		const allPermissions = permissionsA?.concat(permissionsB);
		const reducedPermissions = reduce ? reducePermissions(allPermissions) : allPermissions;
		return { [permissionSet]: removeRedundantPermissions(reducedPermissions) };
	});
	return Object.assign({}, ...mergedPermissionSets);
};
/**
* @param permissionArray
* @returns array of permissions where the permission array has been maximally reduced.
* This is done by filtering out any permission that is a subset of another llpermission with a higher permission
*/
const reducePermissions = (permissionArray) => {
	return permissionArray.filter(({ name: resource, permission }) => {
		const highestPermissionForResource = getPermissionForResource(permissionArray, resource);
		return PERMISSIONS[permission] >= PERMISSIONS[highestPermissionForResource];
	});
};
/**
* @param policy to duplicate
*/
const createPolicy = (policy = EMPTY_POLICY) => ({ ...policy });
/**
* @param policy original policy
* @param type attribute in llpolicy to append newPermission to
* @param newPermission permission to add to the objectstores array of policy
*/
const addPermission = (policy, type, newPermission) => {
	return mergePolicy(policy, Object.assign({}, EMPTY_POLICY, { [type]: [newPermission] }), false);
};
/**
* @param policy
* @param type attribute in llpolicy to append newPermission to
* @param resource the resource to find
* Search pattern:
* 1. Get all matches, and then sort by decreasing permission level
* 2. return the last permission in the list (whatever is the highest permission)
* example input:
*  {
*      objectstores: [
*          {
*              name: '*',
*              permission: 'RW'
*          },
*          {
*              name: '*-hello',
*              permission: 'ADMIN'
*          }
*       ]
*  },
* output if looking for world-hello:  'ADMIN'
*/
const getPermission = (policy, type, resource) => {
	return getPermissionForResource(policy[type], resource);
};
const getPermissionForResource = (set, resource) => {
	const resultPermission = sortPermissionArray(set?.filter(({ name: r }) => micromatch.isMatch(resource, r)) ?? []).shift()?.permission;
	if (resultPermission === DENY || !resource.includes("*")) return resultPermission;
	if ((set?.filter(({ name: r }) => micromatch.isMatch(r, resource)))?.shift()?.permission === DENY) return DENY;
	else return resultPermission;
};
/**
* @param policy
* @param type attribute in llpolicy to append newPermission to
* @param resource the resource to find
*/
const getExactPermission = (policy, type, resource) => policy[type]?.find(({ name: r }) => r === resource)?.permission;
/**
* @param policy
* @param type attribute in llpolicy to remove permission from
* @param resource the resource to remove
*/
const removePermission = (policy, type, resource) => {
	const newPermissions = policy[type]?.filter((permission) => permission.name !== resource);
	return Object.assign(policy, { [type]: newPermissions });
};
/**
* @param policy to validate
* @returns policy is valid
*/
const validate$1 = (policy) => {
	if (!policy) return false;
	return Check(policy);
};
/**
* @param permissionSet to validate
* @returns permission set is valid
*/
const isValidPermissionSet = (permissionSet) => Object.values(PERMISSION_SETS).includes(permissionSet);
//#endregion
//#region src/errors.ts
var errors_exports = /* @__PURE__ */ __exportAll({
	InvalidAction: () => InvalidAction,
	InvalidParameterError: () => InvalidParameterError,
	InvalidPermissionError: () => InvalidPermissionError,
	InvalidPolicyError: () => InvalidPolicyError,
	InvalidSubjectError: () => InvalidSubjectError,
	InvalidTokenError: () => InvalidTokenError,
	PermissionError: () => PermissionError
});
var LLIAMError = class extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
		Error.captureStackTrace(this, this.constructor);
	}
};
var InvalidSubjectError = class extends LLIAMError {
	constructor(subject) {
		super(`Invalid subject id [${subject}]`);
	}
};
var InvalidPolicyError = class extends LLIAMError {
	constructor() {
		super("Policy does not match llpolicy schema");
	}
};
var InvalidPermissionError = class extends LLIAMError {
	constructor() {
		super("Permission does not match llpermission schema");
	}
};
var InvalidAction = class extends LLIAMError {};
var InvalidTokenError = class extends LLIAMError {};
var InvalidParameterError = class extends LLIAMError {};
var PermissionError = class extends LLIAMError {
	constructor(subject, customMessage = false) {
		super(customMessage ? subject : `Subject does not have required permission [${subject}]`);
	}
};
//#endregion
//#region src/token.ts
/**
* @param token a decoded token (payload only)
* @returns the cognito pool id that this token belongs to
*/
const getPoolId = (token) => {
	return token.iss?.split("/").pop();
};
/**
* @param token a decoded token (payload only)
* @returns the subject of this token
*/
const getSubject = (token) => token["sub"];
/**
* @param token a decoded token (payload only)
* @returns the subject of this token
*/
const getUsername = (token) => token["username"];
/**
* @param token a decoded token (payload only)
* @returns the aws region of the cognito pool this token belongs to
*/
const getRegion = (token) => getPoolId(token)?.split("_")[0];
/**
* @param token a decoded token (payload only)
* @returns the cognito groups that this token belongs to
*/
const getGroups = (token) => token["cognito:groups"] || [];
/**
* @param token a decoded (payload only) token
* @param options options passed into the lliam constructor
* @returns if the cognito pool is one that we accept
* @throws {InvalidTokenError} if the cognito pool is invalid
*/
const validateIssuer = (token, options) => {
	const region = getRegion(token) ?? "unknown";
	const poolId = getPoolId(token) ?? "unknown";
	const { userPools } = options;
	if ((userPools ? userPools[region] ?? [] : []).includes(poolId)) return true;
	else throw new InvalidTokenError(`Invalid user pool [${poolId}]`);
};
//#endregion
//#region src/index.ts
const CONSTANTS = {
	EMAIL_ATTRIBUTE,
	GROUPS_ATTRIBUTE,
	STAGE_ATTRIBUTE,
	INTERNAL_ATTRIBUTE,
	PERMISSION_SETS,
	...PERMISSION_SETS,
	ADMIN,
	READWRITE,
	READ,
	OWNER,
	DENY
};
//#endregion
export { CONSTANTS, EMPTY_POLICY, addPermission, createPermission, createPolicy, errors_exports as errors, getExactPermission, getGroups, getPermission, getPermissions, getPoolId, getRegion, getSubject, getUsername, isValidPermissionSet, mergePolicy, permissionIsAtLeast, removePermission, sortPermissionArray, validateIssuer, validate as validatePermission, validate$1 as validatePolicy };

//# sourceMappingURL=index.mjs.map